'use client'

import { useQuery } from '@tanstack/react-query'
import { useRequireAuth } from '~/hooks/useRequireAuth'
import { getAppliedUserId } from '~/app/(dashboard)/_actions/getAppliedUserId'

/**
 * Check if we're in dev/staging environment for logging
 */
const isDevOrStaging = () => {
  const env = process.env.NEXT_PUBLIC_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV
  const nodeEnv = process.env.NODE_ENV
  return (
    env === 'dev' ||
    env === 'staging' ||
    env === 'development' ||
    env === 'preview' ||
    nodeEnv === 'development'
  )
}

/**
 * Hook to get the authoritative member_id from the backend using the signed token.
 *
 * This is the source of truth for member_id - always use this instead of
 * Auth0 metadata, since the backend verifies the token and returns the
 * correct member_id (handles conversion/matching automatically).
 *
 * If the backend member_id differs from the token's member_id, this hook
 * will log a warning but continue using the backend member_id as authoritative.
 * The token will be refreshed naturally on next login or token expiration.
 *
 * Falls back to Auth0 metadata only if backend is unavailable.
 *
 * @returns The applied user ID (authoritative member_id from backend) or null
 */
export function useAppliedUserId() {
  const { user, isLoading: isLoadingUser } = useRequireAuth()

  console.log(
    `[useAppliedUserId] hook called — user=${!!user} isLoadingUser=${isLoadingUser} enabled=${!isLoadingUser}`
  )

  // Fetch the authoritative member_id from backend (reads from token's custom claims)
  // The backend's appliedUserId query reads member_id directly from the JWT token's custom claims
  // This is the source of truth - do NOT fallback to user_metadata which may contain system user ID
  const {
    data: appliedUserId,
    isLoading,
    isError,
    error,
    fetchStatus,
  } = useQuery({
    queryKey: ['applied-user-id'],
    // Don't fetch if user is still loading
    enabled: !isLoadingUser && !!user,
    queryFn: async () => {
      console.log(
        '[useAppliedUserId] queryFn fired — calling getAppliedUserId server action'
      )
      try {
        const backendMemberId = await getAppliedUserId()
        console.log(
          `[useAppliedUserId] server action returned: ${backendMemberId}`
        )
        if (backendMemberId) {
          if (isDevOrStaging()) {
            console.log(
              `[useAppliedUserId] Using applied user ID from backend: ${backendMemberId}`
            )
          }
          return backendMemberId
        } else {
          console.warn(
            '[useAppliedUserId] Backend returned null for appliedUserId'
          )
          return null
        }
      } catch (error) {
        console.error(
          '[useAppliedUserId] Error fetching applied user ID:',
          error
        )
        return null
      }
    },
  })

  console.log(
    `[useAppliedUserId] query state — isLoading=${isLoading} fetchStatus=${fetchStatus} isError=${isError} error=${error} appliedUserId=${appliedUserId}`
  )

  // Return the applied_user_id from backend (authoritative from token) - no fallback to user_metadata
  const memberId = appliedUserId || null

  console.log(
    `[useAppliedUserId] returning memberId=${memberId} isLoading=${
      isLoading || isLoadingUser
    }`
  )

  return {
    memberId,
    isLoading: isLoading || isLoadingUser,
  }
}
